import {Card, DataStatus, Separator, Status} from 'platform/components';
import {Box, Grid, Show} from 'platform/foundation';
import {useFormatNumber} from 'platform/locale';

import {isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetCustomerContractLabourDiscountsQuery} from '@dms/api/customer';
import i18n from '@dms/i18n';

import {TestIdProps, suffixTestId} from 'shared/web';

import {PriceSummaryItem} from '../../PriceSummaryItem/PriceSummaryItem';
import {SummaryItem} from '../../SummaryItem/SummaryItem';

interface ContractWorkDiscountsSectionProps extends TestIdProps {
  customerContractId: string;
}

export function ContractWorkDiscountsSection(props: ContractWorkDiscountsSectionProps) {
  const formatNumber = useFormatNumber();

  const {data, isLoading, isError} = useGetCustomerContractLabourDiscountsQuery({
    customerContractId: props.customerContractId,
  });

  return (
    <Card title={i18n.t('entity.customerContract.labels.workDiscounts')}>
      <DataStatus isLoading={isLoading} isError={isError} minHeight={50}>
        <Show when={isNilOrEmpty(data?.labourDiscount)}>
          <Box paddingVertical={6}>
            <Status headline={i18n.t('general.labels.noData')} />
          </Box>
        </Show>
        {data?.labourDiscount?.map((discount, index) => (
          <Box key={discount?.id}>
            <Show when={index > 0}>
              <Separator />
            </Show>
            <Grid columns={4}>
              <SummaryItem
                isAlternative
                title={i18n.t('general.labels.name')}
                value={discount?.name}
                data-testid={suffixTestId('name', props)}
              />
              <SummaryItem
                isAlternative
                title={i18n.t('entity.customerContract.labels.discountType')}
                value={discount?.discountSettingsType?.name}
                data-testid={suffixTestId('discountType', props)}
              />
              <SummaryItem
                isAlternative
                title={i18n.t('entity.addWork.lables.workCategory')}
                value={discount?.workTypes?.map((workType) => workType?.name).join(', ')}
                data-testid={suffixTestId('workCategory', props)}
              />
              <Show when={isNotNil(discount?.discountedRate)}>
                <PriceSummaryItem
                  title={i18n.t('general.labels.rate')}
                  price={discount?.discountedRate?.amount}
                  currency={discount?.discountedRate?.currency}
                  data-testid={suffixTestId('discountedRate', props)}
                />
              </Show>
              <Show when={isNotNil(discount?.percentageDiscount)}>
                <SummaryItem
                  title={i18n.t('general.labels.discount')}
                  value={`${formatNumber(discount?.percentageDiscount ?? 0)} %`}
                  data-testid={suffixTestId('percentageDiscount', props)}
                />
              </Show>
            </Grid>
          </Box>
        ))}
      </DataStatus>
    </Card>
  );
}
